import { Component, computed, inject, input, Input, OnInit } from '@angular/core';

import { FormArray, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzTreeSelectModule } from 'ng-zorro-antd/tree-select';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzCollapseModule } from 'ng-zorro-antd/collapse';
import { ClassificationCodeService, EhrService } from '@beautyapp/core';
import { DrugListComponent } from '../drug-list/drug-list.component';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { NzModalService } from 'ng-zorro-antd/modal';
import { TranslateService } from '@ngx-translate/core';


@UntilDestroy()
@Component({
  selector: 'beautyapp-patient-anamnesis',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    NzFormModule,
    NzSelectModule,
    NzInputModule,
    NzButtonModule,
    NzTreeSelectModule,
    NzIconModule,
    NzCollapseModule
],
  templateUrl: './patient-anamnesis.component.html',
  styleUrls: ['./patient-anamnesis.component.scss']
})
export class PatientAnamnesisComponent implements OnInit {
  @Input() parentForm!: FormGroup;
  userId = input.required<string>();

  classificationCodeService = inject(ClassificationCodeService);
  modalService = inject(NzModalService);
  translateService = inject(TranslateService);
  ehrService = inject(EhrService);

  immunizationTypes = this.classificationCodeService.getImmunizationTypes();
  bloodComponents = this.classificationCodeService.getBloodComponents();
  drugComponents = this.classificationCodeService.getDrugComponents();

  screeningTypes = [
    { id: 1, value: 'ც ჰეპატიტი' },
    { id: 2, value: 'ბ ჰეპატიტი' },
    { id: 3, value: 'აივ' },
    { id: 4, value: 'სიფილისი' },
    { id: 5, value: 'ტუბერკულოზი' },
  ];

  classificationICDCodes = computed(() => this.classificationCodeService.classificationICDCodes() || []);
  classificationNCSPCodes = computed(() => this.classificationCodeService.classificationNCSPCodes() || []);


  disabilityStatus: any[] = [
    {
      value: 1,
      alias: 'ზომიერი'
    },
    {
      value: 2,
      alias: 'სუსტი'
    },
    {
      value: 3,
      alias: 'მკვეთრი'
    }
  ];


  get anamnesisForm(): FormGroup {
    return this.parentForm.get('anamnesisVitae') as FormGroup;
  }

  get diseaseHistory(): FormArray {
    return this.anamnesisForm.get('diseaseHistory') as FormArray;
  }

  get surgicalInterventions(): FormArray {
    return this.anamnesisForm.get('surgicalInterventions') as FormArray;
  }

  get chronicalDiseases(): FormArray {
    return this.anamnesisForm.get('chronicalDiseases') as FormArray;
  }

  get transfusions(): FormArray {
    return this.anamnesisForm.get('transfusions') as FormArray;
  }

  get alergiesDrugs(): FormArray {
    return this.anamnesisForm.get('alergiesDrugs') as FormArray;
  }

  get otherAllergiesAndReactions(): FormArray {
    return this.anamnesisForm.get('otherAllergiesAndReactions') as FormArray;
  }

  get immunization(): FormArray {
    return this.anamnesisForm.get('immunization') as FormArray;
  }

  get screening(): FormArray {
    return this.anamnesisForm.get('screening') as FormArray;
  }

  constructor() {
    if (this.classificationICDCodes().length === 0) {
      this.classificationCodeService.getIcdCodes();
    }

    if (this.classificationNCSPCodes().length === 0) {
      this.classificationCodeService.getNCSPCodes();
    }
  }

  ngOnInit(): void {
    // Ensure the parent form has the anamnesisVitae group
    if (!this.parentForm.get('anamnesisVitae')) {
      this.parentForm.addControl('anamnesisVitae', new FormGroup({
        disabilityStatus: new FormControl<string | null>(null),
        diseaseHistory: new FormArray([]),
        surgicalInterventions: new FormArray([]),
        chronicalDiseases: new FormArray([]),
        transfusions: new FormArray([]),
        alergiesDrugs: new FormArray([]),
        otherAllergiesAndReactions: new FormArray([]),
        immunization: new FormArray([]),
        screening: new FormArray([])
      }));
    }

    // Load anamnesis data if parent hasn't already populated it
    const uid = this.userId();
    if (uid) {
      const av = this.parentForm.get('anamnesisVitae');
      const hasData = (av?.get('diseaseHistory') as FormArray)?.length > 0
        || (av?.get('surgicalInterventions') as FormArray)?.length > 0
        || (av?.get('chronicalDiseases') as FormArray)?.length > 0
        || (av?.get('transfusions') as FormArray)?.length > 0
        || (av?.get('alergiesDrugs') as FormArray)?.length > 0
        || (av?.get('immunization') as FormArray)?.length > 0;
      if (!hasData) {
        this.getAnamnesis(uid);
      }
    }
  }

  getAnamnesis(userId: string) {
    this.ehrService.getAnamnesis(userId)
      .pipe(
        untilDestroyed(this)
      )
      .subscribe(res => {
        console.log(res);
        if (res) {
          this.anamnesisForm.get('disabilityStatus')?.setValue(res.disabilityStatus);

          this.diseaseHistory.clear();
          (res.diseaseHistory || []).forEach((m: any) => {
            this.diseaseHistory.push(new FormGroup({
              icd10Id: new FormControl<string | null>(m.icd10Id != null ? String(m.icd10Id) : null),
              year: new FormControl<string | null>(m.year),
              doctorClarification: new FormControl<string | null>(m.doctorClarification || m.clinicalDiagnosis),
            }));
          });

          this.surgicalInterventions.clear();
          (res.surgicalInterventions || []).forEach((m: any) => {
            this.surgicalInterventions.push(new FormGroup({
              ncspId: new FormControl<string | null>(m.ncspId != null ? String(m.ncspId) : null),
              year: new FormControl<string | null>(m.year),
              doctorComment: new FormControl<string | null>(m.doctorComment)
            }));
          });

          this.chronicalDiseases.clear();
          (res.chronicalDiseases || []).forEach((m: any) => {
            this.chronicalDiseases.push(new FormGroup({
              icd10Id: new FormControl<string | null>(m.icd10Id != null ? String(m.icd10Id) : null),
              year: new FormControl<string | null>(m.year),
              doctorClarification: new FormControl<string | null>(m.doctorClarification || m.clinicalDiagnosis),
            }));
          });

          this.transfusions.clear();
          (res.transfusions || []).forEach((m: any) => {
            this.transfusions.push(new FormGroup({
              bloodComponent: new FormControl<number | null>(m.bloodComponent),
              transfDate: new FormControl<string | null>(m.transfDate),
              doctorComment: new FormControl<string | null>(m.doctorComment)
            }));
          });

          this.alergiesDrugs.clear();
          (res.alergiesDrugs || []).forEach((m: any) => {
            this.alergiesDrugs.push(new FormGroup({
              drugId: new FormControl<number | null>(m.drugId),
              drugName: new FormControl<string | null>(m.drug?.geoTradeName || null),
              drugComponent: new FormControl<number | null>(m.drugComponent != null ? +m.drugComponent : null),
              comment: new FormControl<string | null>(m.comment)
            }));
          });

          this.otherAllergiesAndReactions.clear();
          (res.otherAllergiesAndReactions || []).forEach((m: any) => {
            this.otherAllergiesAndReactions.push(new FormGroup({
              comment: new FormControl<string | null>(m.comment)
            }));
          });

          this.immunization.clear();
          (res.immunization || []).forEach((m: any) => {
            this.immunization.push(new FormGroup({
              yAxisID: new FormControl<number | null>(m.yAxisID),
              dateOfInnection: new FormControl<string | null>(m.dateOfInnection),
              vaccineName: new FormControl<string | null>(m.vaccineName)
            }));
          });

          this.screening.clear();
          (res.screening || []).forEach((m: any) => {
            this.screening.push(new FormGroup({
              screeningType: new FormControl<number | null>(m.screeningType != null ? +m.screeningType : null),
              screeningDate: new FormControl<string | null>(m.screeningDate),
              screeningResult: new FormControl<number | null>(m.screeningResult != null ? +m.screeningResult : null),
              doctorClarification: new FormControl<string | null>(m.doctorClarification),
            }));
          });
        }
      });
  }

  addDisease() {
    this.diseaseHistory.push(new FormGroup({
      icd10Id: new FormControl<string | null>(null),
      year: new FormControl<string | null>(null),
      doctorClarification: new FormControl<string | null>(null),
    }));
  }

  removeDisease(index: number) {
    this.diseaseHistory.removeAt(index);
  }

  addSurgicalIntervention() {
    this.surgicalInterventions.push(new FormGroup({
      ncspId: new FormControl<string | null>(null),
      year: new FormControl<string | null>(null),
      doctorComment: new FormControl<string | null>(null)
    }));
  }

  removeSurgicalIntervention(index: number) {
    this.surgicalInterventions.removeAt(index);
  }


  addChronicalDisease() {
    this.chronicalDiseases.push(new FormGroup({
      icd10Id: new FormControl<string | null>(null),
      year: new FormControl<string | null>(null),
      doctorClarification: new FormControl<string | null>(null),
    }));
  }

  removeChronicalDisease(index: number) {
    this.chronicalDiseases.removeAt(index);
  }

  addTransfusion() {
    this.transfusions.push(new FormGroup({
      bloodComponent: new FormControl<string | null>(null),
      transfDate: new FormControl<string | null>(null),
      doctorComment: new FormControl<string | null>(null)
    }));
  }

  removeTransfusion(index: number) {
    this.transfusions.removeAt(index);
  }

  addAllergyDrug() {
    this.alergiesDrugs.push(new FormGroup({
      drugId: new FormControl<string | null>(null),
      drugName: new FormControl<string | null>(null),
      drugComponent: new FormControl<string | null>(null),
      comment: new FormControl<string | null>(null)
    }));
  }

  removeAllergyDrug(index: number) {
    this.alergiesDrugs.removeAt(index);
  }

  addOtherAllergy() {
    this.otherAllergiesAndReactions.push(new FormGroup({
      comment: new FormControl<string | null>(null)
    }));
  }

  removeOtherAllergy(index: number) {
    this.otherAllergiesAndReactions.removeAt(index);
  }

  addImmunization() {
    this.immunization.push(new FormGroup({
      yAxisID: new FormControl<string | null>(null),
      dateOfInnection: new FormControl<string | null>(null),
      vaccineName: new FormControl<string | null>(null)
    }));
  }

  removeImmunization(index: number) {
    this.immunization.removeAt(index);
  }

  addScreening() {
    this.screening.push(new FormGroup({
      screeningType: new FormControl<number | null>(null),
      screeningDate: new FormControl<string | null>(null),
      screeningResult: new FormControl<number | null>(null),
      doctorClarification: new FormControl<string | null>(null),
    }));
  }

  removeScreening(index: number) {
    this.screening.removeAt(index);
  }

  chooseDrug(control: any) {
    this.modalService.create({
      nzOkDisabled: true,
      nzCancelText: this.translateService.instant('general.close'),
      nzTitle: this.translateService.instant('ERX.chooseProductTitle'),
      nzContent: DrugListComponent,
      nzWidth: '60%'
    }).afterClose.pipe(
      untilDestroyed(this)
    ).subscribe(res => {
      console.log(res);
      if (res) {
        control.get('drugId').setValue(res.id);
        control.get('drugName').setValue(res.geoTradeName);
      }
    });
  }
}
